import React, { useCallback, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { formOperation } from "../../wrappers/formOperation";
import {
    CasePropertyReferredDocumentsForm,
    TCasePropertyReferredDocumentsForm,
} from "../../../../domain/codecs/form/CasePropertyReferredDocumentsForm";
import { UuidForm } from "../../../../domain/codecs/form/UuidForm";
import { TCasePropertyReferredDocument } from "../../../../domain/codecs/CasePropertyReferredDocument";
import { toDisplayString as casesDocumentTypeToDisplayString } from "../../../../domain/models/CaseDocumentType1";
import { registerDescriptionToCasesDocumentsType } from "../../../../domain/codecs/LandRegistry";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { FontRoboto } from "../BuildingBlocks/FontRoboto";
import { TextColor } from "../BuildingBlocks/TextColor";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../WeightBold/WeightBold";
import { WeightMedium } from "../WeightMedium/WeightMedium";
import { WeightSemiBold } from "../WeightSemiBold/WeightSemiBold";
import { CRMButtonTertiary } from "../CRMButtonTertiary/CRMButtonTertiary";
import { CRMStatusPill } from "../Simple/CRMStatusPill/CRMStatusPill";
import { CRMCardOutsidePopupFormSubmit } from "../CRMCardOutsidePopupFormSubmit/CRMCardOutsidePopupFormSubmit";
import { CRMInputLabelAndErrorWrapComponent } from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { THexColor } from "../../models/StringLiterals";

type TRowStatus = "not_ordered" | "ordered" | "manual_referral" | "unavailable" | "errored";

const rowStatus = (row: TCasePropertyReferredDocument): TRowStatus =>
    row.ordered_at !== null ? "ordered"
    : row.marked_unavailable_at !== null ? "unavailable"
    : row.marked_manual_referral_at !== null ? "manual_referral"
    : row.last_errored_at !== null ? "errored"
    : "not_ordered";

const statusLabel = (s: TRowStatus): string => {
    switch (s) {
        case "not_ordered": return "Available";
        case "ordered": return "Ordered";
        case "manual_referral": return "Manual referral";
        case "unavailable": return "Unavailable";
        case "errored": return "Errored";
    }
};

type TStatusVisual = { pillColor: THexColor; textColor: THexColor };

const statusVisual = (s: TRowStatus): TStatusVisual => {
    switch (s) {
        case "ordered": return { pillColor: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2, textColor: CRMColors.NEUTRAL_PAPER };
        case "not_ordered": return { pillColor: CRMColors.HIGHLIGHTS_CALMING_PINK_2, textColor: CRMColors.NEUTRAL_PAPER };
        case "manual_referral": return { pillColor: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2, textColor: CRMColors.NEUTRAL_PAPER };
        case "unavailable": return { pillColor: CRMColors.NEUTRAL_4, textColor: CRMColors.NEUTRAL_PAPER };
        case "errored": return { pillColor: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2, textColor: CRMColors.NEUTRAL_PAPER };
    }
};

const formatDate = (iso: string): string =>
    DateTime.fromISO(iso).toFormat("dd MMM yyyy");

type TProps = {
    casePropertyId: string;
    refetchSignal?: number;
};

type TRowItemProps = {
    row: TCasePropertyReferredDocument;
    isOrderInFlight: boolean;
    onOrderClick: () => void;
};

const RowItem = ({ row, isOrderInFlight, onOrderClick }: TRowItemProps): JSX.Element => {
    const status = rowStatus(row);
    const typeLabel = casesDocumentTypeToDisplayString(
        registerDescriptionToCasesDocumentsType(row.register_description),
    );
    const visual = statusVisual(status);
    const showOrderButton = status === "not_ordered" || status === "errored";
    const downloadUrl = row.case_document_file_id === null || row.cases_documents_id === null
        ? null
        : `${env.REACT_APP_API_URL}/web/cases/${row.case_id}/documents/${row.cases_documents_id}/files/${row.case_document_file_id}/download`;

    return (
        <Background
            color={CRMColors.NEUTRAL_PAPER}
            padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
            borderRadius="5px"
            borderDefinition={`1px solid ${CRMColors.NEUTRAL_8}`}
        >
            <SpacingColumn spacing={CRMSpacing.TINY}>
                <SpacingRow
                    spacing={CRMSpacing.SMALL}
                    childSize={["1fr", "auto", ...(showOrderButton ? ["auto"] : []), ...(downloadUrl !== null ? ["auto"] : [])].join(" ")}
                    alignItems="center"
                >
                    <FontQuicksand>
                        <Text size={16} lineHeight={1.25}>
                            <WeightBold>{typeLabel}</WeightBold>
                        </Text>
                    </FontQuicksand>

                    <CRMStatusPill
                        label={statusLabel(status)}
                        pillColor={visual.pillColor}
                        borderColor={visual.pillColor}
                        textColor={visual.textColor}
                    />

                    {showOrderButton && (
                        <CRMButtonTertiary
                            label={status === "errored" ? "Retry" : "Order"}
                            icon="file-clip"
                            onClick={onOrderClick}
                            disabled={isOrderInFlight}
                        />
                    )}

                    {downloadUrl !== null && (
                        <CRMButtonTertiary
                            label="Open"
                            icon="open"
                            href={downloadUrl}
                            target="_blank"
                        />
                    )}
                </SpacingRow>

                <FontRoboto>
                    <TextColor color={CRMColors.NEUTRAL_2}>
                        <Text size={13} lineHeight={1.3}>
                            {formatDate(row.document_date)}
                            {" · "}
                            {row.entry_numbers.join(", ")}
                            {row.filed_under && (
                                <>{" · Filed under "}<WeightSemiBold>{row.filed_under}</WeightSemiBold></>
                            )}
                        </Text>
                    </TextColor>
                </FontRoboto>

                {row.rejection_message && (
                    <FontRoboto>
                        <TextColor color={CRMColors.NEUTRAL_2}>
                            <Text size={12} lineHeight={1.3}>
                                <WeightMedium>{row.rejection_message}</WeightMedium>
                            </Text>
                        </TextColor>
                    </FontRoboto>
                )}
            </SpacingColumn>
        </Background>
    );
};

export const CRMCasePropertyReferredDocumentsTable = (props: TProps): JSX.Element => {
    const [form, setForm] = useState<TCasePropertyReferredDocumentsForm | null>(null);
    const [pendingOrderRow, setPendingOrderRow] = useState<TCasePropertyReferredDocument | null>(null);
    const [inFlightOrderId, setInFlightOrderId] = useState<string | null>(null);

    const load = useCallback(async () => {
        const result = await formOperation("GetCasePropertyReferredDocuments", {
            ...CasePropertyReferredDocumentsForm.newDefault(),
            edited: { id: props.casePropertyId },
            original: { id: props.casePropertyId },
        });
        setForm(result);
    }, [props.casePropertyId]);

    useEffect(() => {
        load();
    }, [load, props.refetchSignal]);

    const order = async (rowId: string) => {
        setInFlightOrderId(rowId);
        await formOperation("OrderCasePropertyReferredDocument", {
            ...UuidForm.newDefault(),
            edited: { id: rowId },
            original: { id: rowId },
        });
        setInFlightOrderId(null);
        setPendingOrderRow(null);
        await load();
    };

    const body =
        form === null ? (
            <CRMParagraph><WeightMedium>Loading…</WeightMedium></CRMParagraph>
        ) : form.children.referred_documents.length === 0 ? (
            <CRMParagraph><WeightMedium>No referred documents found on this title.</WeightMedium></CRMParagraph>
        ) : (
            <SpacingColumn spacing={CRMSpacing.TINY}>
                {form.children.referred_documents.map((row) => (
                    <RowItem
                        key={row.id}
                        row={row}
                        isOrderInFlight={inFlightOrderId !== null}
                        onOrderClick={() => setPendingOrderRow(row)}
                    />
                ))}
            </SpacingColumn>
        );

    return (
        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
            <CRMInputLabelAndErrorWrapComponent
                label="Documents referred to on this title"
                guidance="Documents named in entries on the title register and filed separately at HMLR. Click Order copy to pull them in via OC2 — £7 per document."
            >
                {body}
            </CRMInputLabelAndErrorWrapComponent>

            <CRMCardOutsidePopupFormSubmit
                isOpen={pendingOrderRow !== null}
                title="Order this document from HMLR?"
                context="warning"
                closeText="Cancel"
                ctaText="Yes, order from HMLR"
                onClose={() => setPendingOrderRow(null)}
                onCTA={() => { if (pendingOrderRow !== null) { order(pendingOrderRow.id); } }}
                formStatus={inFlightOrderId !== null ? "submitting" : "requiresSubmission"}
                validationErrors={[]}
                size="medium"
            >
                {pendingOrderRow !== null && (
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <CRMParagraph>
                            <WeightSemiBold>
                                You are about to order a copy of the {pendingOrderRow.register_description} dated {formatDate(pendingOrderRow.document_date)} from HM Land Registry.
                            </WeightSemiBold>
                        </CRMParagraph>
                        <CRMParagraph>
                            <WeightBold>
                                This will charge £7 to our HMLR account. The document will appear in the doc store once received (typically 2–5 seconds).
                            </WeightBold>
                        </CRMParagraph>
                    </SpacingColumn>
                )}
            </CRMCardOutsidePopupFormSubmit>
        </SpacingColumn>
    );
};
